import { faArrowDown, faArrowRight, faCircleUser, faCodeBranch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Metadata, MetadataInfo, ProductSearchResult } from 'models/SearchResult'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import React from 'react'
import { prettyDisplayValue } from 'constants/Constants'
import { User } from 'hooks/useUsers'
import { BaseModal } from 'components/modals/BaseModal'
import { Link } from 'react-router-dom'
import { prettyDateFromTimestamp } from './Comment'
import { isApproved, isArchived, isEditsRequested, isPendingReview, isReadyForApproval } from 'utils/Metadata'
import { isATypeOfUpissue, isChangeRequestForProductRequiringUpissue, isUpissueProduct } from 'utils/ActiveAction'

export const StatusTag = ({metadata, className = ``} : {metadata: Metadata, className?: string }) => {
    
    const isUpissue: boolean = isATypeOfUpissue(metadata?.activeAction)

    return (
    <>
        <span
            className={
            `tag 
            ${className}  
            ${isPendingReview(metadata)
            ? 'is-info'
            : isEditsRequested(metadata)
            ? 'is-warning'
            : isArchived(metadata) 
            ? 'is-danger'
            : 'is-primary'
            }`}>
            {isUpissue && <FontAwesomeIcon className={`pr-2`} icon={faCodeBranch} size={`1x`}/> }
            {prettyDisplayValue(metadata.status)}
        </span>
    </>

  )
}

export const IssueNumberTag = ({ metadataInfo, issueNumber, actionText, tagSize = 'is-medium' }:{ metadataInfo: MetadataInfo, issueNumber?: number, actionText: string, tagSize?: string}) => (
    <span className={`has-tooltip-arrow has-tooltip-multiline tag is-rounded ${tagSize} is-pulled-right has-text-weight-bold p-4`} data-tooltip={metadataInfo && `${actionText} ${metadataInfo.byFullName} on ${prettyDateFromTimestamp(metadataInfo.timestamp)}`}>
        <FontAwesomeIcon className="pr-1" size="1x" icon={faCodeBranch} /> {`v${issueNumber || 1}`} 
    </span>
)    

const MetadataInfoDisplay = ({ metadata, loggedInUser, actionText = '' }:{ metadata: MetadataInfo, loggedInUser: User, actionText?: string }) => (
    <div className="columns is-vcentered">
        <div className="column is-narrow p-0">
            <FontAwesomeIcon icon={faCircleUser} size={'1x'} />
        </div>
        <div className="column is-narrow">
            {metadata && <span>{`${metadata.byFullName} ${metadata.byUserId === loggedInUser.user_id ? `(You)` : ``} ${actionText}`}</span>}
        </div>
    </div>
)

const Comment = ({ comment }: { comment: string }) => (
    comment &&
        <article className="message is-light">
            <div className="message-body">
            <p className="pl-3">"{comment}"</p>
            </div>
        </article>
)

const VersionLink = ({ productID, version, linkText }: { productID: string, version: number, linkText: string }) => (
    <Link className="has-text-grey underline-on-hover" target='_blank' to={`/view/product/${productID}/versions/${version}`}>
        {linkText}<FontAwesomeIcon className="pl-1" icon={faArrowRight} />
    </Link>
)

export const isCreated = (searchResult: ProductSearchResult) => searchResult?.metadata?.version === 0
export const isRestored = (current: ProductSearchResult, previous: ProductSearchResult): boolean => isApproved(current?.metadata) && isArchived(previous?.metadata)

const VersionHistoryModal = ({
    productVersions,
    isModalActive,
    isLoading,
    loggedInUser,
    setIsVersionHistoryModalActive,
}:{
    productVersions: ProductSearchResult[],
    isModalActive: boolean,
    isLoading: boolean,
    loggedInUser: User,
    setIsVersionHistoryModalActive: (isActive: boolean) => void
}) => {


    return (
        <BaseModal 
            isModalActive={isModalActive}
            modalTitle={`Version History`}
            onCancelModal={() => setIsVersionHistoryModalActive(false)}>

                <div className="columns is-vcentered p-4">
                    <div className="column">
                        {isLoading
                        ?
                        <PageLoadSpinner />
                        :
                        productVersions && productVersions.length > 0
                        ?
                        <div className="columns">
                            <div className="column">
                            <div className="timeline">
                                {productVersions.map((pv, idx) => {

                                return (
                                    <div key={`${pv.objectID}-${pv?.metadata.version}-${idx}`}>
                                    <header className="timeline-header">
                                    {isCreated(pv) && isPendingReview(pv?.metadata)
                                        ?
                                        <span className={`tag is-primary`}>{`Created`}</span>
                                        :
                                        idx > 0 && isRestored(pv, productVersions[idx-1])
                                        ?
                                        <span className={`tag is-primary is-light`}>{`Restored`}</span>
                                        :
                                        <StatusTag metadata={pv?.metadata} />
                                    }
                                    </header>
                            
                                    {pv?.metadata.upissues.includes(pv?.metadata?.version) && 
                                    <div className="timeline-item">
                                        <div className="timeline-marker is-icon">     
                                            <IssueNumberTag metadataInfo={pv?.metadata.lastUpissued} issueNumber={pv?.metadata.issueNumber} actionText={`Last issued by `} />                         
                                        </div>
                                    </div>
                                    }

                                    <div className="timeline-item">
                                    <div className="timeline-marker is-icon">
                                        <FontAwesomeIcon icon={faArrowDown} />
                                    </div>
                                    <div className="timeline-content">
                                        <p className="heading">
                                        {isCreated(pv)
                                        ?
                                        pv.metadata?.created && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata?.created.timestamp} />
                                        :
                                        isUpissueProduct(pv?.metadata?.activeAction) && pv.metadata?.activeAction?.initiatedOnVersion === pv.metadata.version - 1  && !isChangeRequestForProductRequiringUpissue(pv?.metadata?.activeAction)
                                        ?
                                        pv?.metadata.lastEdited && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastEdited.timestamp} />
                                        : 
                                        idx > 0 && isRestored(pv, productVersions[idx-1])
                                        ?
                                        pv?.metadata.lastRestored && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastRestored.timestamp} />
                                        :                                       
                                        isPendingReview(pv?.metadata)
                                        ?
                                        pv?.metadata.lastEdited && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastEdited.timestamp} />
                                        :
                                        isEditsRequested(pv?.metadata) || isApproved(pv.metadata) 
                                        ?
                                        pv?.metadata.lastModerated && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastModerated.timestamp} />
                                        :
                                        isArchived(pv?.metadata) 
                                        ?
                                        pv?.metadata.lastArchived && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastArchived.timestamp} />
                                        :
                                        isReadyForApproval(pv?.metadata)
                                        ?
                                        pv?.metadata.lastModerated && <VersionLink productID={pv.objectID} version={pv?.metadata.version} linkText={pv?.metadata.lastModerated.timestamp} />
                                        :
                                        ''
                                        }
                                        </p>
                                        
                                        <div className="columns"> 
                                            <div className="column">  

                                                {isCreated(pv)
                                                ?
                                                pv?.metadata?.created &&
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata?.created}
                                                        loggedInUser={loggedInUser} 
                                                        actionText={pv.metadata?.created.creationType === 'NEW_PRODUCT_LINE' ? `added a new product line` : `added a product`} />                                                    
                                                :
                                                isUpissueProduct(pv.metadata?.activeAction) && pv.metadata?.activeAction?.initiatedOnVersion === pv.metadata.version - 1  && !isChangeRequestForProductRequiringUpissue(pv?.metadata?.activeAction)
                                                ?
                                                pv?.metadata.lastEdited &&
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata.lastEdited}
                                                        loggedInUser={loggedInUser} 
                                                        actionText='upissued a product for review' />     
                                                :
                                                isPendingReview(pv.metadata)
                                                ?
                                                    <MetadataInfoDisplay
                                                        metadata={pv.metadata.lastEdited}
                                                        loggedInUser={loggedInUser}
                                                        actionText='made edits' />
                                                :
                                                isEditsRequested(pv?.metadata)
                                                ?
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata.lastModerated}
                                                        loggedInUser={loggedInUser}
                                                        actionText='reviewed and requested edits' />
                                                :
                                                idx > 0 && isRestored(pv, productVersions[idx-1])
                                                ?
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata.lastRestored}
                                                        loggedInUser={loggedInUser}
                                                        actionText='restored this product' />
                                                :
                                                isApproved(pv?.metadata) 
                                                ?
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata.lastModerated}
                                                        loggedInUser={loggedInUser}
                                                        actionText='reviewed and approved' />
                                                : isArchived(pv?.metadata)
                                                ?
                                                    <MetadataInfoDisplay
                                                        metadata={pv?.metadata.lastArchived}
                                                        loggedInUser={loggedInUser}
                                                        actionText='archived' />
                                                :
                                                isReadyForApproval(pv?.metadata)
                                                ?
                                                <MetadataInfoDisplay
                                                    metadata={pv?.metadata.lastModerated}
                                                    loggedInUser={loggedInUser}
                                                    actionText='marked as ready for approval' />
                                                :
                                                ''
                                                }

                                            </div> 
                                        </div>

                                        <div className="columns"> 
                                            <div className="column pt-0">  
                                                {isUpissueProduct(pv?.metadata?.activeAction) && !isChangeRequestForProductRequiringUpissue(pv?.metadata?.activeAction)
                                                ?
                                                <Comment comment={pv?.metadata.lastEdited?.comment} />
                                                :
                                                isPendingReview(pv?.metadata)
                                                ?
                                                <Comment comment={pv?.metadata.lastEdited.comment} />
                                                :
                                                isEditsRequested(pv?.metadata) || isApproved(pv?.metadata) 
                                                ?
                                                <Comment comment={pv?.metadata.lastModerated?.comment} />
                                                :
                                                ''
                                                }
                                            </div> 
                                        </div>

                                    </div>
                                    </div>
                                </div>
                                )}
                                )}
                                <div className="timeline-header">
                                    <span className="tag is-primary is-light">Current</span>
                                </div>
                            </div>
                            </div>
                            </div>
                        :
                        <p>There is no version history for this product.</p>
                        }
                    </div>
                </div>
        </BaseModal>
    )
}

export { VersionHistoryModal }